import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { SinglePackagingModel } from '../../../models/single-packaging.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TransportMode, LanguageService, minValidator, DangerousGoodItem, NotificationService } from '@hazmatsuite-next/ui';
import { EMPTY, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { EnumPackageType } from '../../../models/package-type.enum';
import { PackagingType } from '../../../models/packaging-type.model';
import { EasyDgdService } from '../../../services/easydgd.service';
import { PackagingModel } from '../../../models/packaging.model';
import { GefahrgutApiDangerousGoodItem } from '../../../models/gefahrgut-api-dangerous-good-item.interface';
import { CanLoadTogetherResult } from '../../../models/can-load-together-result.interface';

@UntilDestroy()
@Component({
  selector: 'lib-easydgd-single-packaging-modal',
  templateUrl: './single-packaging-modal.component.html',
  styleUrls: ['./single-packaging-modal.component.scss'],
})
export class SinglePackagingModalComponent implements OnInit, OnChanges {
  @Input() show = false;
  @Input() isDgSelectionMode = false;
  @Input() showLoadFromArticles = false;
  @Input() transportMode: TransportMode = TransportMode.Unknown;
  @Input() title = '';
  @Input() packageTypes: PackagingType[] = [];
  @Input() packagingUnCodes: PackagingType[] = [];
  @Input() set package(pkg: SinglePackagingModel) {
    if (!pkg) {
      this.model = { TransportMode: this.transportMode } as SinglePackagingModel;
      this.easyDgdService.setUuidIfNecessary(this.model);
      return;
    }

    this.model = { ...pkg };

    this.formGroup.reset({
      properShippingName: pkg.DangerousGood?.DefaultShippingExpression,
      useAlternativeProperShippingName: pkg.UseAlternativeProperShippingName,
      technicalName: pkg.TechnicalName,
      isEnvironmentallyHazardous: pkg.IsEnvironmentallyHazardous || pkg.IsEnvironmentallyHazardousFromDb,
      packagingType: +pkg.PackagingType,
      packagingUnCode: pkg.PackagingUnCode,
      useAlternativePackagingType: pkg.UseAlternativePackagingType,
      alternativePackagingType: pkg.AlternativePackagingType,
      netWeight: pkg.NetWeightValue,
      netVolume: pkg.NetVolumeValue,
      grossWeight: pkg.GrossWeightValue,
      netExplosiveMass: pkg.NetExplosiveMassValue,
      isTotalQuantity: pkg.IsTotalQuantity,
    });
    if (pkg.IsTechnicalNameRequired) {
      this.technicalName?.enable();
    }
    this.easyDgdService.setUuidIfNecessary(this.model);
  };

  @Input() isEditMode = false;
  @Input() allDangerousGoods: GefahrgutApiDangerousGoodItem[] = [];

  @Output() closeModal = new EventEmitter<SinglePackagingModel>();
  @Output() loadUnPackagingTypes = new EventEmitter<number>();

  model: SinglePackagingModel = new SinglePackagingModel();
  formGroup = new FormGroup({
    properShippingName: new FormControl({ value: '', disabled: true }),
    alternativeProperShippingName: new FormControl('', (ctrl: AbstractControl) =>
      this.model?.UseAlternativeProperShippingName ? Validators.required(ctrl) : null),
    useAlternativeProperShippingName: new FormControl(false),
    technicalName: new FormControl({ value: '', disabled: true }, (ctrl: AbstractControl) =>
      this.model?.DangerousGood?.IsTechnicalNameRequired ? Validators.required(ctrl) : null),
    isEnvironmentallyHazardous: new FormControl({ value: false, disabled: this.model?.IsEnvironmentallyHazardousFromDb ?? false }),
    lowerFlashpoint: new FormControl('', (ctrl: AbstractControl) =>
      this.model?.IsFlashpointRequired ? Validators.required(ctrl) : null),
    countOfPackagings: new FormControl(1, [Validators.required, Validators.min(1)]),
    packagingType: new FormControl(1, (ctrl: AbstractControl) =>
      this.model?.UseAlternativePackagingType ? null : Validators.required(ctrl)
    ),
    useAlternativePackagingType: new FormControl(false),
    packagingUnCode: new FormControl('1A1', (ctrl: AbstractControl) =>
      this.packagingUnCodes?.length ? Validators.required(ctrl) : null
    ),
    alternativePackagingType: new FormControl('', (ctrl: AbstractControl) =>
      this.model?.UseAlternativePackagingType ? Validators.required(ctrl) : null
    ),
    isTotalQuantity: new FormControl(false, Validators.required),
    netWeight: new FormControl('', [(ctrl: AbstractControl) =>
      this.model?.IsNetWeightMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.model?.IsNetWeightMandatory ? minValidator()(ctrl) : null]
    ),
    netVolume: new FormControl('', [(ctrl: AbstractControl) =>
      this.model?.IsNetVolumeMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.model?.IsNetVolumeMandatory ? minValidator()(ctrl) : null]
    ),
    grossWeight: new FormControl('', [(ctrl: AbstractControl) =>
      this.model?.IsGrossWeightMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.model?.IsGrossWeightMandatory ? minValidator()(ctrl) : null]
    ),
    netExplosiveMass: new FormControl('', [(ctrl: AbstractControl) =>
      this.model?.IsNetExplosiveMassMandatory ? Validators.required(ctrl) : null, (ctrl: AbstractControl) => this.model?.IsNetExplosiveMassMandatory ? minValidator()(ctrl) : null]
    ),
  });

  packageType: EnumPackageType = EnumPackageType.NotSet;

  public singlePackagingResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['SinglePackagingResource'])
  );

  public packingListResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['PackingListResource']));

  constructor(
    private languageService: LanguageService,
    private easyDgdService: EasyDgdService,
    private cd: ChangeDetectorRef,
    private notificationService: NotificationService
  ) {
    this.languageService.fetchResources(['SinglePackagingResource', 'PackingListResource']);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['packagingUnCodes']) {
      if (this.packagingUnCode?.touched) {
        this.packagingUnCode.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  ngOnInit(): void {
    this.packagingType?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(val => {
      this.packagingUnCode?.setValue(undefined);
      this.loadUnPackagingTypes.emit(val);
    });
  }

  public selectDangerousGood(dangerousGood: DangerousGoodItem, msg: string): void {
    this.easyDgdService.canLoadTogether(dangerousGood.ID, this.transportMode, this.allDangerousGoods).pipe(
      take(1),
      switchMap((res: CanLoadTogetherResult) => {
        if (res.IsLoadTogetherAccepted) {
          return this.easyDgdService.initializeTempPackage(dangerousGood.ID, EnumPackageType.SinglePacking, this.transportMode).pipe(
            tap((packagingModel: PackagingModel) => {
              this.model = packagingModel as SinglePackagingModel;
              this.model.DangerousGood = dangerousGood;
              this.model.DangerousGoodId = dangerousGood.ID;
              this.model.IsEnvironmentallyHazardous = dangerousGood.IsEnvironmentallyHazardous;
              this.model.IsTechnicalNameRequired = dangerousGood.IsTechnicalNameRequired;
              this.isDgSelectionMode = false;
              this.cd.detectChanges();
            }));
        } else {
          this.notificationService.showErrorMessage(`${msg ?? 'Can not load together with:'} ${res.ConflictItems.map(dg => dg.Name).join(', ')}`);
          return of(EMPTY);
        }
      }))
      .subscribe();
  }

  public markAsTouched(): void {
    this.formGroup?.markAllAsTouched();
  }

  addPackage() {
    this.closeModal.emit(this.getModel());
    this.formGroup?.reset({}, { emitEvent: false });
    this.isDgSelectionMode = true;
  }

  cancel() {
    this.closeModal.emit();
    this.formGroup?.reset({}, { emitEvent: false });
    this.isDgSelectionMode = true;
  }

  get properShippingName(): AbstractControl | null | undefined {
    return this.formGroup?.get('properShippingName');
  }
  get alternativeProperShippingName(): AbstractControl | null | undefined {
    return this.formGroup?.get('alternativeProperShippingName');
  }
  get useAlternativeProperShippingName(): AbstractControl | null | undefined {
    return this.formGroup?.get('useAlternativeProperShippingName');
  }
  get technicalName(): AbstractControl | null | undefined {
    return this.formGroup?.get('technicalName');
  }
  get isEnvironmentallyHazardous(): AbstractControl | null | undefined {
    return this.formGroup?.get('isEnvironmentallyHazardous');
  }
  get lowerFlashpoint(): AbstractControl | null | undefined {
    return this.formGroup?.get('lowerFlashpoint');
  }
  get countOfPackagings(): AbstractControl | null | undefined {
    return this.formGroup?.get('countOfPackagings');
  }
  get packagingType(): AbstractControl | null | undefined {
    return this.formGroup?.get('packagingType');
  }
  get useAlternativePackagingType(): AbstractControl | null | undefined {
    return this.formGroup?.get('useAlternativePackagingType');
  }
  get packagingUnCode(): AbstractControl | null | undefined {
    return this.formGroup?.get('packagingUnCode');
  }
  get alternativePackagingType(): AbstractControl | null | undefined {
    return this.formGroup?.get('alternativePackagingType');
  }
  get netWeight(): AbstractControl | null | undefined {
    return this.formGroup?.get('netWeight');
  }
  get netVolume(): AbstractControl | null | undefined {
    return this.formGroup?.get('netVolume');
  }
  get grossWeight(): AbstractControl | null | undefined {
    return this.formGroup?.get('grossWeight');
  }
  get netExplosiveMass(): AbstractControl | null | undefined {
    return this.formGroup?.get('netExplosiveMass');
  }
  get isTotalQuantity(): AbstractControl | null | undefined {
    return this.formGroup?.get('isTotalQuantity');
  }

  private getModel(): SinglePackagingModel {
    return {
      ...this.model,
      UseAlternativeProperShippingName: this.useAlternativeProperShippingName?.value,
      UseAlternativePackagingType: this.useAlternativePackagingType?.value,
      ProperShippingName: this.properShippingName?.value,
      AlternativeProperShippingName: this.alternativeProperShippingName?.value,
      AlternativePackagingType: this.alternativePackagingType?.value,
      IsTechnicalNameRequired: this.model?.IsTechnicalNameRequired ?? false,
      TechnicalName: this.technicalName?.value,
      IsEnvironmentallyHazardous: this.isEnvironmentallyHazardous?.value,
      LowerFlashpoint: this.lowerFlashpoint?.value,
      PackagingType: this.packagingType?.value,
      PackagingUnCode: this.packagingUnCode?.value,
      NetWeightValue: this.netWeight?.value,
      NetWeightUnitSymbol: this.model?.NetWeightUnitSymbol ?? '',
      NetVolumeValue: this.netVolume?.value,
      NetVolumeUnitSymbol: this.model?.NetVolumeUnitSymbol ?? '',
      GrossWeightValue: this.grossWeight?.value,
      GrossWeightUnitSymbol: this.model?.GrossWeightUnitSymbol ?? '',
      NetExplosiveMassValue: this.netExplosiveMass?.value,
      NetExplosiveMassUnitSymbol: this.model?.NetExplosiveMassUnitSymbol ?? '',
      CountOfPackagings: this.countOfPackagings?.value,
      IsTotalQuantity: JSON.parse(this.isTotalQuantity?.value),
    } as SinglePackagingModel;
  }
}
